import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, first, Observable, of, tap } from 'rxjs';

import {
  ITokenPurchase,
  ITokensBalance,
  ITokensPackageResponse
} from '../interfaces/token.interfaces';
import { IConversationHistoryResponse } from 'app/interfaces/conversations.interfaces';
import { IMessage } from 'app/interfaces/conversations.interfaces';

import { environment } from 'environments/environment';
import { ELocalStorageKey } from '../enums/shared.enums';

@Injectable({
  providedIn: 'root'
})
export class TokensService {
  private readonly apiUrl = `${environment.apiBaseUrl}`;
  tokensBalanceSubject = new BehaviorSubject<number>(null);
  tokensBalance$ = this.tokensBalanceSubject.asObservable();
  private currentBalance: number | null = null;
  getMoreTokensEvent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  successTokenMediaPurchaseEvent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pendingMediaPurchaseMessage$ = new BehaviorSubject<IMessage | null>(null);
  getMediaAfterTokenPurchase$: BehaviorSubject<IConversationHistoryResponse> =
    new BehaviorSubject<IConversationHistoryResponse>(null);

  constructor(private readonly http: HttpClient) {}

  getTokensBalance(): Observable<ITokensBalance> {
    const endpoint = this.apiUrl + `/tokens/balance`;
    return this.http.get<ITokensBalance>(endpoint);
  }

  purchaseTokens(requestBody: ITokenPurchase): Observable<ITokensBalance> {
    const endpoint = this.apiUrl + `/tokens/purchase`;
    return this.http.post<ITokensBalance>(endpoint, requestBody).pipe(
      tap((response: ITokensBalance) => {
        // Update balance after purchase
        this.tokensBalanceSubject.next(response.data.balance);
        this.currentBalance = response.data.balance;
      })
    );
  }

  getTokensPackage(): Observable<ITokensPackageResponse> {
    const endpoint = this.apiUrl + `/tokens/packages`;
    return this.http.get<ITokensPackageResponse>(endpoint);
  }

  fetchTokensBalance(emitOnlyOnCompletion?: boolean): Observable<ITokensBalance> {
    const user = JSON.parse(localStorage.getItem(ELocalStorageKey.User));
    if (!user) return of();
    // Only fetch if we don't have a balance yet, or if we specifically want a refresh
    if (this.currentBalance !== null && !emitOnlyOnCompletion) {
      return of({ data: { balance: this.currentBalance } } as ITokensBalance);
    }

    const balance$ = this.getTokensBalance().pipe(
      first(),
      tap({
        next: (res: ITokensBalance) => {
          this.tokensBalanceSubject.next(res.data.balance);
          this.currentBalance = res.data.balance;
        },
        error: () => {
          this.tokensBalanceSubject.next(0);
          this.currentBalance = 0;
        }
      })
    );

    if (emitOnlyOnCompletion) {
      return balance$;
    } else {
      balance$.subscribe();
      return of();
    }
  }

  getCurrentBalance(): number | null {
    return this.currentBalance;
  }
}
